import { forwardRef } from 'react';
import Box from '@mui/material/Box';
import { brand } from '@/shared/config/brand';

export type PromoFormat = 'portrait' | 'square' | 'landscape';

export const PROMO_SIZES: Record<PromoFormat, { width: number; height: number }> = {
  portrait: { width: 1080, height: 1350 },
  square: { width: 1080, height: 1080 },
  landscape: { width: 1200, height: 630 },
};

export const TRIPPY_SRC = '/images/Trippy.png';

const SAGE_DEEP = '#0F1A0F';
const SAGE = '#1A2E1A';
const GOLD = '#C8A96E';
const GOLD_SOFT = '#E9D7AE';
const CREAM = '#F9F4E8';

/**
 * Per-format layout. Each format is laid out for its own aspect ratio instead
 * of one design being stretched, so nothing important is ever cropped.
 */
const LAYOUT: Record<
  PromoFormat,
  {
    pad: number;
    logo: number;
    eyebrow: number;
    headline: number;
    sub: number;
    body: number;
    cta: number;
    footer: number;
    textWidth: number;
    trippyHeight: number;
    trippyRight: number;
    trippyBottom: number;
    spot: number;
    gap: number;
  }
> = {
  portrait: {
    pad: 80, logo: 64, eyebrow: 24, headline: 92, sub: 40, body: 30, cta: 32, footer: 24,
    textWidth: 900, trippyHeight: 640, trippyRight: -30, trippyBottom: 96, spot: 600, gap: 30,
  },
  square: {
    pad: 72, logo: 56, eyebrow: 22, headline: 72, sub: 34, body: 26, cta: 28, footer: 22,
    textWidth: 600, trippyHeight: 600, trippyRight: -60, trippyBottom: 72, spot: 560, gap: 24,
  },
  landscape: {
    pad: 56, logo: 44, eyebrow: 16, headline: 50, sub: 24, body: 18, cta: 20, footer: 16,
    textWidth: 640, trippyHeight: 520, trippyRight: -10, trippyBottom: 0, spot: 470, gap: 16,
  },
};

interface PromoPostCanvasProps {
  format: PromoFormat;
}

/**
 * The shareable promo image, drawn at its real pixel size so `html-to-image`
 * exports it 1:1. The referral link is not printed on it: it travels in the
 * caption, so one image works for every guide and every channel.
 */
export const PromoPostCanvas = forwardRef<HTMLDivElement, PromoPostCanvasProps>(function PromoPostCanvas(
  { format },
  ref
) {
  const { width, height } = PROMO_SIZES[format];
  const l = LAYOUT[format];
  const portrait = format === 'portrait';

  return (
    <Box
      ref={ref}
      sx={{
        position: 'relative',
        width,
        height,
        overflow: 'hidden',
        color: CREAM,
        fontFamily: '"Inter", system-ui, sans-serif',
        background: `radial-gradient(ellipse 70% 60% at 85% 20%, rgba(200,169,110,0.22) 0%, transparent 60%),
          linear-gradient(150deg, ${SAGE} 0%, ${SAGE_DEEP} 55%, #0B140B 100%)`,
      }}
    >
      {/* Topographic rings: a quiet travel/map texture. */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          inset: 0,
          opacity: 0.09,
          backgroundImage: `repeating-radial-gradient(circle at 12% 88%, transparent 0 34px, ${GOLD} 35px 36px)`,
        }}
      />

      {/* Spotlight that lifts Trippy's black tee off the dark background. */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          width: l.spot,
          height: l.spot,
          right: l.trippyRight + (portrait ? 40 : 30),
          bottom: l.trippyBottom + (portrait ? 40 : 10),
          borderRadius: '50%',
          background: `radial-gradient(circle at 45% 40%, ${GOLD_SOFT} 0%, ${GOLD} 55%, #9C7F45 100%)`,
          boxShadow: `0 0 0 ${Math.round(l.spot * 0.06)}px rgba(200,169,110,0.18), 0 0 0 ${Math.round(l.spot * 0.13)}px rgba(200,169,110,0.08)`,
        }}
      />

      <Box
        component="img"
        src={TRIPPY_SRC}
        alt=""
        crossOrigin="anonymous"
        sx={{
          position: 'absolute',
          height: l.trippyHeight,
          width: 'auto',
          right: l.trippyRight,
          bottom: l.trippyBottom,
          filter: 'drop-shadow(0 24px 40px rgba(0,0,0,0.45))',
        }}
      />

      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          height: '100%',
          boxSizing: 'border-box',
          p: `${l.pad}px`,
          display: 'flex',
          flexDirection: 'column',
          gap: `${l.gap}px`,
        }}
      >
        <Box component="img" src={brand.logoUrl} alt="trplin" crossOrigin="anonymous" sx={{ height: l.logo, width: 'auto', alignSelf: 'flex-start' }} />

        <Box sx={{ maxWidth: l.textWidth, mt: portrait ? `${l.gap}px` : 0 }}>
          <Box
            sx={{
              display: 'inline-block',
              px: `${l.eyebrow * 0.8}px`,
              py: `${l.eyebrow * 0.35}px`,
              mb: `${l.gap * 0.8}px`,
              borderRadius: 999,
              border: `2px solid ${GOLD}`,
              color: GOLD_SOFT,
              fontSize: l.eyebrow,
              fontWeight: 700,
              letterSpacing: '0.18em',
            }}
          >
            JOIN TRPLIN AS A TRAVEL GUIDE
          </Box>
          <Box
            sx={{
              fontFamily: '"Playfair Display", Georgia, serif',
              fontWeight: 800,
              fontSize: l.headline,
              lineHeight: 1.04,
              letterSpacing: '-0.01em',
              color: '#FFFFFF',
            }}
          >
            LOVE TRAVEL?{' '}
            <Box component="span" sx={{ color: GOLD }}>
              TURN IT INTO SOMETHING MORE.
            </Box>
          </Box>
          <Box
            sx={{
              mt: `${l.gap}px`,
              fontSize: l.body,
              lineHeight: 1.5,
              color: 'rgba(249,244,232,0.86)',
              maxWidth: portrait ? 600 : l.textWidth,
            }}
          >
            Connect with travellers. Share your local knowledge. Grow with a community that loves exploring.
          </Box>
        </Box>

        <Box
          sx={{
            alignSelf: 'flex-start',
            mt: portrait ? `${l.gap}px` : 0,
            px: `${l.cta * 1.1}px`,
            py: `${l.cta * 0.55}px`,
            borderRadius: 999,
            bgcolor: GOLD,
            color: SAGE_DEEP,
            fontSize: l.cta,
            fontWeight: 800,
            boxShadow: '0 12px 30px rgba(200,169,110,0.35)',
          }}
        >
          Join me on Trplin →
        </Box>

        <Box
          sx={{
            mt: 'auto',
            pt: `${l.gap * 0.6}px`,
            borderTop: `1px solid rgba(200,169,110,0.35)`,
            maxWidth: format === 'landscape' ? 600 : l.textWidth,
            fontSize: l.footer,
            letterSpacing: '0.06em',
            color: 'rgba(249,244,232,0.75)',
          }}
        >
          Your local travel journey starts here · {brand.domain}
        </Box>
      </Box>
    </Box>
  );
});
